import BaseNodeModel from "../BaseNodeModel";
import { NoInputTimeoutOverride } from "../../form_components";
import { Constant } from '../../const';
import { ConfigViewType, NodeType } from "../../types";
import { ObjectDropdown } from "../../page-components";

export default class InputNodeModel extends BaseNodeModel {
    /**
     * Input node model
     * @module InputNodeModel
     * @param {string} nodeName Node display name
     * @param {number} x Node x coordinate on canvas
     * @param {number} y Node y coordinate on canvas
     * @param {Object} props Pre-defined node properties; if non-null, apply values to node properties
     * @param {string} id If non-null, use this ID as the node ID
     */
    constructor(nodeName = "UNTITLED_NODE", x = 0, y = 0, props = null, id = null) {
        super(nodeName, x, y, null, id);
        this.props.nodeTypeName = "Input";
        this.props.colorClass = "blue";
        this.props.iconClass = "mic";
        this.DEFAULT_COLOR_CLASS = "blue";
        this.applyProps(props);
        this.validateExpectedIntent();
    }

    setConfig(key, value) {
        if (key === "expectedIntent") {
            // If set, add to local intents if not exist
            if (value) {
                if (!this.props.config.localIntents) {
                    this.props.config.localIntents = [];
                }
                const intIndex = this.props.config.localIntents.findIndex(element => element === value);
                if (intIndex === -1) {
                    this.props.config.localIntents.push(value);
                    document.dispatchEvent(new CustomEvent("ev-property-item-list-refresh"));
                }
            }
        }

        if (key === "localIntents") {
            if (this.props.config.localIntents && value) {
                // Diff
                const shorten = this.props.config.localIntents.length > value.length;
                // If intent is expected and is removed, remove expected
                if (shorten) {
                    for (var i = 0; i < this.props.config.localIntents.length; i++) {
                        const ix = value.findIndex(element => element === this.props.config.localIntents[i]);
                        if (ix === -1 ) {
                            const item = this.props.config.localIntents[i];
                            //If the item deleted from local intents is the same intent assigned to expectedIntent remove intent as expectedIntent
                            if (this.props.config.expectedIntent && item === this.props.config.expectedIntent) {
                                this.props.config.expectedIntent = "";
                            }
                            //If the item deleted from local intents is the same intent assigned to dtmfIntent remove intent as dtmfIntent
                            if (this.props.config.dtmfIntent && item === this.props.config.dtmfIntent) {
                                this.props.config.dtmfIntent = "";
                            }
                        }
                    }
                }
            document.dispatchEvent(new CustomEvent("ev-property-refresh"));
            }
        }
        if (key == "DTMF") {
            //If DTMF is marked as false, remove the intent from dtmfIntent.
            if(value === false && this.props.config.dtmfIntent!==""){
                this.props.config.dtmfIntent = "";
            }
            document.dispatchEvent(new CustomEvent("ev-property-refresh"));
        }
        if (key === "inputType" && value === "YN_GENERIC") {
            this.props.config.localIntents = this.props.config.localIntents ?
                this.props.config.localIntents : [];
            const intents = window.apm.knowledgeBase.getIntents();
            const nIntent = intents.filter(intent => intent.name === "NO");
            const yIntent = intents.filter(intent => intent.name === "YES");
            if (this.props.config.localIntents.findIndex(el => el === nIntent[0].value) === -1) this.props.config.localIntents.push(nIntent[0].value);
            if (this.props.config.localIntents.findIndex(el => el === yIntent[0].value) === -1) this.props.config.localIntents.push(yIntent[0].value);
            document.dispatchEvent(new CustomEvent("ev-property-refresh"));
            document.dispatchEvent(
                new CustomEvent("ev-property-item-list-refresh")
            );
        }

        if ((key === "dtmfInterDigitTimeout" || key === "maxIntentTimeout" || key === "lengthOfSilence") && value !== "") {
            value = Number(value);
        }
        super.setConfig(key, value);
        this.validateExpectedIntent();
    }

    validateExpectedIntent() {
        if (!window.apm || !window.apm.knowledgeBase) {
            return;
        }
        const inputTypes = window.apm.knowledgeBase.getInputTypes();
        const inputType = inputTypes.find(it => it.value === this.props.config.inputType);
        if (!inputType) {
            return;
        }
        const hasExpectedIntentError = inputType.required && !this.props.config.expectedIntent;
        const errorKey = "expectedIntentError";
        const error = this.getPropLabel("expected_intent") + " is required";
        if (hasExpectedIntentError) {
            this.setError(errorKey, error);
            document.dispatchEvent(new CustomEvent("ev-property-refresh-error", {
                detail: {
                    target: "expectedIntent",
                    error: error
                }
            }));
        } else {
            this.removeError(errorKey);
            document.dispatchEvent(new CustomEvent("ev-property-refresh-error", {
                detail: {
                    target: "expectedIntent",
                    error: null
                }
            }));
        }
        window.apm.postEventToCurrentView({ target: "request-refresh"});
    }

    /**
     * Get possible output for this input node; can be consumed by out-transition
     */
    getOutputSuggestions() {
        const suggestion = super.getOutputSuggestions();

        var values = [];
        if (this.props.config.localIntents) {
            const intents = window.apm.knowledgeBase.getIntents();
            for (var i = 0; i < this.props.config.localIntents.length; i++) {
                const selection = window.apm.knowledgeBase.getIntentIndexFromValue(this.props.config.localIntents[i]);
                if (selection < 0) {
                    continue;
                }
                const optionName = intents[selection].name;
                values.push(optionName);
            }
        }
        suggestion.value = values;

        return suggestion;
    }

    getUnusedIntents() {
        if (!window.apm || !window.apm.knowledgeBase) {
            return;
        }

        const usedIntents = this.getLinks().reduce((prev, link) => prev.concat(
            link.getConfig('condition').length ? link.getConfig('condition').join() : []
        ), []).join();
        let localIntents = this.getConfig('localIntents') || [];
        localIntents = window.apm.knowledgeBase.getIntents().filter(intent => localIntents.includes(intent.value));
        const unusedIntents = localIntents.reduce((prev, intent) => prev.concat(
            !usedIntents.includes(intent.name) ? intent.value : []
        ), []);

        return unusedIntents;
    }

    checkUnusedIntents() {
        const unusedIntents = this.getUnusedIntents();
        // Reset validation error
        if(this.props.error) {
            const { [Constant.k.VALIDATION_ERROR]: _, ...rest } = this.props.error;
            const unusedError = unusedIntents.length ? {[Constant.k.VALIDATION_ERROR]: "Unused Local Intent"} : {};
            this.props.error = { ...rest, ...unusedError }
        }

        window.apm.postEventToCurrentView({ target: "request-refresh"});
    }

    getConfigViews() {

        this.checkUnusedIntents();
        const {
            disambiguationPromptId,
            noInputTimeoutOverride
        } = this.props.config;

        /** @type {import("../../types").ConfigView[]} */
        const configView = [
            {},
            {},
            {},
            {},
            { type: "Input", label: this.getPropLabel("maxIntentTimeout"), key: "maxIntentTimeout", hint: this.getPropLabel("time_in_milliseconds"), tooltip: "The total length of an utterance that can possibly be given. Defaulted to 50000" },
            { type: "Input", label: this.getPropLabel("lengthOfSilence"), key: "lengthOfSilence", hint: this.getPropLabel("time_in_milliseconds"), tooltip: "The time after caller stops talking that is required before end of speech is fired.Defaulted to 2000"},
            {
                content: (
                    <ObjectDropdown
                        onChange={(newValue) => {
                            this.setConfigAndParent('disambiguationPromptId', newValue);
                        }}
                        type={NodeType.prompt}
                        useStateValue
                        value={disambiguationPromptId}
                    />
                ),
                label: this.getPropLabel("disambiguationPromptId"),
                tooltip: this.getPropLabel("disambiguationPromptIdTooltip"),
                type: ConfigViewType.jsx
            },
            {
                content: (
                    <NoInputTimeoutOverride
                        onValueChange={(newValue) => {
                            this.setConfigAndParent('noInputTimeoutOverride', newValue);
                        }}
                        value={noInputTimeoutOverride}
                    />
                ),
                label: this.getPropLabel("noInputTimeout"),
                tooltip: this.getPropLabel("noInputTimeout_tooltip"),
                type: ConfigViewType.jsx,
            },
            {
                type: "IntentDropdown", 
                key: "expectedIntent", 
                label: this.getPropLabel("expected_intent"), 
                optionName: this.getPropLabel("intent_opt"), 
                removable: true,
                tooltip: "The intent that you would expect ASR to use when returning data to the application"
            },
            {
                type: "IntentDropdown", key: "impliedIntent", label: this.getPropLabel("implied_intent"), optionName: this.getPropLabel("intent_opt"), tooltip: "When checking compound intents for this input node, the implied intent will be added as if the user had given it too.", removable: true
            },
            {
                type: "Dropdown", key: "globalHandling", label: this.getPropLabel("global_handling"), optionName: this.getPropLabel("global_handling_opt"),
                options: [
                    { name: "All", value: "ALL" }, { name: "Conversational only", value: "CONVERSATIONAL_ONLY" }
                ]
            },
            { type: "ItemList", label: this.getPropLabel("local_intent"), key: "localIntents", view: "localIntent" },
            { type: "Input", label: this.getPropLabel("analyst_class"), key: "analystClass", suggestion: "variable"},
            { type: "Switch", key: "ignoreDroppableIntents", label: this.getPropLabel("ignore_droppable_intents"), checked: false },
            { type: "Switch", key: "ignoreCompoundIntents", label: this.getPropLabel("ignore_compound_intents"), checked: false },

        ];
        this.props.configView = [
            {
                type: "Dropdown", key: "inputType", label: this.getPropLabel("input_type"), optionName: this.getPropLabel("input_type_opt"),
                options: window.apm.knowledgeBase.getInputTypes()
            },
            { type: "Switch", key: "sensitiveData", label: this.getPropLabel("sensitive_data"), checked: false },
            { type: "Switch", key: "DTMF", label: this.getPropLabel("dtmf"), checked: false},
            { type: "Switch", key: "dtmfLiveAgent", label: this.getPropLabel("dtmf_live_agent"), checked: false},
        ];

        if (this.getConfig("DTMF")) {
            configView[0] = { type: "Switch", key: "dtmfOnly", label: this.getPropLabel("dtmf_only"), checked: false },
            //adding removable: true , option to remive intent as expected intent
            configView[1] = {   type: "DtmfDropDown", 
                                key: "dtmfIntent", 
                                label: this.getPropLabel("dtmf_intent"), 
                                removable: true,
                                optionName: this.getPropLabel("intent_opt"), 
                                tooltip: "Choose an intent from \"Intents handled locally\" list below."},
                configView[2] = { type: "Input", label: this.getPropLabel("dtmfInterDigitTimeout"), key: "dtmfInterDigitTimeout", hint: this.getPropLabel("time_in_milliseconds"), tooltip: "How long the voice browser will wait between DTMF key presses before assuming someone is done"},
                configView[3] = { type: "Input", label: this.getPropLabel("dtmfTerminationKey"), key: "dtmfTerminationKey", hint: this.getPropLabel("typically_a_hash"), tooltip: "What key ends DTMF input." }

        }
        return [...this.getConfigViewNameField(),
        ...[
            { type: "Textarea", label: this.getPropLabel("description"), key: "description" },
            { type: "ItemList", label: this.getPropLabel("recognition_variables"), key: "inputs", view: "argument" },
            { type: "ItemList", label: this.getPropLabel("outputs"), key: "outputs", view: "return" }
        ],
        ...this.props.configView,

        ...configView,

        ...[
            { type: "hr" },
            { type: "Textarea", label: this.getPropLabel("note"), key: "note" }
        ]
        ];
    }
}